"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function StrategicInsightsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/3] w-full animate-pulse rounded-xl bg-muted" />
            <div className="h-6 w-24 animate-pulse rounded-md bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-md bg-muted" />
            <div className="h-16 w-full animate-pulse rounded-md bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground">Unable to load insights at this time.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-8 text-center">
        <p className="text-muted-foreground">No insights published yet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => {
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "Insight";
      const imageUrl = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577001/img/generic_consulting_office_abstract.png";
      return <Link key={post.id} to={post.link || "#"} data-index={i} className="group block h-full">
            <Card className="h-full border-none bg-transparent shadow-none transition-all duration-300">
              <div className="mb-6 aspect-[4/3] w-full overflow-hidden rounded-xl bg-muted">
                <img src={imageUrl} alt={post.title} className="h-full w-full object-cover saturate-50 transition-all duration-500 group-hover:scale-105 group-hover:saturate-100" />
              </div>
              <CardHeader className="p-0">
                <Badge className="mb-4 w-fit border-transparent bg-chart-5 text-white hover:bg-chart-5/90">
                  {category}
                </Badge>
                <CardTitle className="line-clamp-2 text-2xl font-bold text-primary transition-colors group-hover:text-secondary" dangerouslySetInnerHTML={{
              __html: post.title
            }} />
              </CardHeader>
              <CardContent className="mt-4 p-0">
                {post.excerpt && <div className="line-clamp-3 text-muted-foreground" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-primary transition-colors group-hover:text-secondary">
                  Read Article <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </div>
              </CardContent>
            </Card>
          </Link>;
    })}
    </div>;
}
export default function StrategicInsights() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="strategic-insights" className="bg-background py-24 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-16 text-center">
          <h2 className="font-serif text-3xl font-bold tracking-tight text-primary md:text-5xl">
            Strategic Insights
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">Expert insights on artificial intelligence, cloud computing, automation, and digital transformation strategies for modern businesses.<div><br /></div><div>Our insights hub provides practical guidance for business leaders looking to understand emerging technologies and implement AI, cloud strategy, and digital transformation initiatives successfully.</div></p>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <StrategicInsightsConsumer />
        </PostsProvider>

        <div className="mt-16 flex justify-center">
          <Link to="/InsightsPage" className="inline-flex items-center gap-2 text-lg font-medium text-primary transition-colors hover:text-primary/80">
            <span className="underline decoration-secondary decoration-2 underline-offset-8">
              View All Insights
            </span>
            <ArrowRight className="size-5" />
          </Link>
        </div>
      </div>
    </section>;
}